import { useCallback, useRef, useState } from "react";
import { Image as ImageIcon, Loader2, UploadCloud, RefreshCcw } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// PhotoUpload
//
// Drop-in replacement for the product image tap-to-upload pattern. Accepts:
//   - Drag & drop (whole file dropped anywhere on the frame)
//   - Click / tap to open the native file picker
//   - Mobile camera (accept="image/*" gives Safari/Android their native chooser)
//
// Client-side pipeline:
//   1. Load the picked file into an ImageBitmap
//   2. Centre-crop to a square using the user's `objectPosition` focal point
//   3. Downscale onto a 1200 × 1200 canvas → JPEG blob at ~0.85 quality
//   4. Emit a File named "<original>.jpg" to the parent via `onFile(File)`
//
// Reposition: after a file is loaded, the seller can drag the image inside
// the square frame to change the focal point. Every pointer-up re-encodes
// the JPEG so the parent always holds the latest cropped File.
//
// Product images use a 1:1 aspect ratio EVERYWHERE in Gate + Gather. Do not
// change the output dimensions or aspect here without also touching the
// storefront tiles (StorePublic) and the Dashboard product list.
// ─────────────────────────────────────────────────────────────────────────────

const OUTPUT_SIZE = 1200;
const JPEG_QUALITY = 0.85;

async function fileToBitmap(file) {
  // createImageBitmap handles EXIF orientation on modern browsers.
  return await createImageBitmap(file, { imageOrientation: "from-image" });
}

async function bitmapToSquareFile(bitmap, focal, zoom, originalName) {
  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext("2d");
  // zoom > 1 → we sample a SMALLER square of the source and stretch it up to
  // OUTPUT_SIZE. Focal point stays anchored so what the seller centred in the
  // preview is what we crop.
  const baseSize = Math.min(bitmap.width, bitmap.height);
  const srcSize = baseSize / Math.max(1, zoom);
  const maxSX = Math.max(0, bitmap.width - srcSize);
  const maxSY = Math.max(0, bitmap.height - srcSize);
  const sx = (focal.x / 100) * maxSX;
  const sy = (focal.y / 100) * maxSY;
  ctx.drawImage(bitmap, sx, sy, srcSize, srcSize, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", JPEG_QUALITY));
  const base = (originalName || "photo").replace(/\.[^.]+$/, "");
  return new File([blob], `${base}.jpg`, { type: "image/jpeg" });
}

export default function PhotoUpload({
  initialPreview = null,
  onFile,
  busy = false,
  testid = "photo-upload",
}) {
  const [preview, setPreview] = useState(initialPreview);
  const [bitmap, setBitmap] = useState(null);
  const [origName, setOrigName] = useState("photo");
  const [focal, setFocal] = useState({ x: 50, y: 50 });
  const [zoom, setZoom] = useState(1);
  const [isDragOver, setIsDragOver] = useState(false);
  const [processing, setProcessing] = useState(false);
  const dragState = useRef(null);
  const inputRef = useRef(null);

  const emitProcessed = useCallback(async (bm, f, z, name) => {
    if (!bm || !onFile) return;
    setProcessing(true);
    try {
      const file = await bitmapToSquareFile(bm, f, z, name);
      onFile(file);
    } finally {
      setProcessing(false);
    }
  }, [onFile]);

  const handleFile = useCallback(async (file) => {
    if (!file || !file.type?.startsWith("image/")) return;
    setOrigName(file.name);
    const url = URL.createObjectURL(file);
    setPreview(url);
    const bm = await fileToBitmap(file);
    setBitmap(bm);
    const centre = { x: 50, y: 50 };
    setFocal(centre);
    setZoom(1);
    await emitProcessed(bm, centre, 1, file.name);
  }, [emitProcessed]);

  const onInputChange = (e) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const onDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const onDragLeave = () => setIsDragOver(false);

  // Pointer-drag inside the frame → shift focal point (0-100% each axis).
  const onPointerDown = (e) => {
    if (!bitmap) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragState.current = {
      pid: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startFocal: { ...focal },
      rect: e.currentTarget.getBoundingClientRect(),
    };
  };
  const onPointerMove = (e) => {
    const s = dragState.current;
    if (!s || s.pid !== e.pointerId) return;
    const dxPct = ((e.clientX - s.startX) / s.rect.width) * 100;
    const dyPct = ((e.clientY - s.startY) / s.rect.height) * 100;
    // Drag right → focal point moves LEFT (feels like panning the photo).
    setFocal({
      x: Math.max(0, Math.min(100, s.startFocal.x - dxPct)),
      y: Math.max(0, Math.min(100, s.startFocal.y - dyPct)),
    });
  };
  const onPointerUp = async (e) => {
    const s = dragState.current;
    if (!s || s.pid !== e.pointerId) return;
    dragState.current = null;
    // Re-encode with the final focal point so the parent holds a File that
    // matches what the seller sees.
    if (bitmap) await emitProcessed(bitmap, focal, zoom, origName);
  };

  const onZoomChange = async (e) => {
    const z = Number(e.target.value) / 100; // slider 100-250 → zoom 1.00-2.50
    setZoom(z);
  };
  const onZoomCommit = async () => {
    if (bitmap) await emitProcessed(bitmap, focal, zoom, origName);
  };

  const openPicker = () => inputRef.current?.click();
  const replace = (e) => { e.stopPropagation(); openPicker(); };

  const hasImage = !!preview;

  return (
    <div data-testid={testid}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={onInputChange}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      <div
        onClick={hasImage ? undefined : openPicker}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onPointerDown={hasImage ? onPointerDown : undefined}
        onPointerMove={hasImage ? onPointerMove : undefined}
        onPointerUp={hasImage ? onPointerUp : undefined}
        onPointerCancel={hasImage ? onPointerUp : undefined}
        className={[
          "relative aspect-square rounded-2xl overflow-hidden border-2 border-dashed",
          "flex items-center justify-center transition-colors select-none",
          hasImage ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
          isDragOver
            ? "border-[#3F674E] bg-[#3F674E]/8"
            : "border-stone-300 bg-stone-50 hover:bg-stone-100",
        ].join(" ")}
      >
        {hasImage ? (
          <>
            <img
              src={preview}
              alt="preview"
              draggable={false}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover pointer-events-none"
              style={{
                objectPosition: `${focal.x}% ${focal.y}%`,
                transform: `scale(${zoom})`,
                transformOrigin: `${focal.x}% ${focal.y}%`,
              }}
              data-testid={`${testid}-preview`}
            />
            {(processing || busy) && (
              <div className="absolute inset-0 bg-white/40 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-[#20251F]" />
              </div>
            )}
            <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-[11px]">
              <span className="bg-white/90 rounded-full px-2 py-0.5 text-stone-700 shadow-sm">
                Drag to reposition
              </span>
              <button
                type="button"
                onClick={replace}
                data-testid={`${testid}-replace`}
                className="bg-white/90 rounded-full px-2 py-0.5 text-[#3F674E] font-medium shadow-sm inline-flex items-center gap-1"
              >
                <RefreshCcw className="w-3 h-3" /> Replace
              </button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center text-stone-500 px-4 text-center pointer-events-none">
            {isDragOver ? (
              <>
                <UploadCloud className="w-8 h-8 text-[#3F674E]" />
                <span className="mt-2 text-sm text-[#3F674E]">Drop to add</span>
              </>
            ) : (
              <>
                <ImageIcon className="w-8 h-8" />
                <span className="mt-2 text-sm font-medium text-[#20251F]">Add a photo</span>
                <span className="mt-1 text-[11px] text-stone-500 leading-snug">
                  Drag &amp; drop or tap. Square photos work best —
                  <br className="hidden sm:inline" /> don't worry, we'll resize it for you.
                </span>
              </>
            )}
          </div>
        )}
      </div>
      {hasImage && (
        <div className="mt-2 flex items-center gap-3" data-testid={`${testid}-zoom-row`}>
          <span className="font-mono-label text-[10px] uppercase tracking-[0.14em] text-[#6B6E67] shrink-0">Zoom</span>
          <input
            type="range"
            min="100"
            max="250"
            step="1"
            value={Math.round(zoom * 100)}
            onChange={onZoomChange}
            onPointerUp={onZoomCommit}
            onTouchEnd={onZoomCommit}
            data-testid={`${testid}-zoom`}
            className="flex-1 accent-[#20251F]"
            aria-label="Zoom"
          />
          <span className="font-mono-label text-[10px] text-[#6B6E67] tabular-nums w-10 text-right">
            {zoom.toFixed(1)}×
          </span>
        </div>
      )}
    </div>
  );
}
